import classnames from 'classnames';
import _ from 'lodash';
import type React from 'react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import useDropdownInput from './useDropdownInput';

export type DropdownInputProps = {
  container?: string;
  className?: string;
  name?: string;
  label?: string;
  options: (string | number)[];
  value: string | number;
  onChange?: (name: string | undefined, value: string | number) => void;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  isWarning?: boolean;
  warningMessage?: string;
  warningLinkText?: string;
  handleClickWarningLinkText?: (e: React.MouseEvent) => void;
  needReplacedText?: string;
  selectedItemIcon?: React.ReactNode;
  displayValue?: string;
  showCaret?: boolean;
  direction?: 'up' | 'down' | 'left' | 'right';
  dropdownToggleId?: string;
};

/**
 * Strips a fixed substring out of a dropdown option value before display. Coerces the value to a string and removes the first occurrence of needReplacedText with lodash replace.
 *
 * @param value - Raw option value to clean up.
 * @param needReplacedText - Substring removed from the stringified value.
 * @returns The stringified value with the first occurrence of the substring removed.
 */
const handleReplaceText = (value: string | number, needReplacedText: string) => {
  let replacedText = value;
  replacedText = _.replace(String(replacedText), needReplacedText, '');
  return replacedText;
};

const DropdownInput = ({
  className = '',
  name = '',
  label = '',
  onChange = () => {},
  disabled = false,
  isError = false,
  errorMessage = '',
  isWarning = false,
  warningMessage = '',
  warningLinkText = '',
  handleClickWarningLinkText = () => {},
  needReplacedText = '',
  selectedItemIcon,
  displayValue = '',
  showCaret = true,
  direction = 'down',
  container,
  options,
  value,
  dropdownToggleId,
}: DropdownInputProps) => {
  const { state, toggleDisplayDropdown, onClickDropdownItem } = useDropdownInput({
    name,
    onChange,
  });

  return (
    <div className={classnames('custom-dropdown-input', className)}>
      {!!label && <div className="custom-dropdown-input__label">{label}</div>}
      <Dropdown isOpen={state.isOpenDropdown} toggle={toggleDisplayDropdown} direction={direction}>
        <DropdownToggle
          id={dropdownToggleId}
          className={classnames('custom-dropdown-toggle', isError ? '--error' : '')}
          caret={showCaret}
          disabled={disabled}
        >
          {selectedItemIcon}
          {displayValue ||
            (value ? (needReplacedText ? handleReplaceText(value, needReplacedText) : value) : '')}
        </DropdownToggle>
        <DropdownMenu
          flip={false}
          // * Render to body to prevent scrollbar flicker when open menu
          className={classnames(container === 'body' ? 'custom-dropdown-menu-body' : '')}
          container={container}
        >
          {_.map(options, (option, index) => (
            <DropdownItem
              key={index}
              active={option === value}
              onClick={() => {
                onClickDropdownItem(option);
              }}
            >
              {option}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
      {isError && <div className="custom-dropdown-input__error-message">{errorMessage}</div>}
      {isWarning && (
        <div className="custom-dropdown-input__warning-container">
          <div className="custom-dropdown-input__warning-message">{warningMessage}</div>
          <Button
            className="custom-dropdown-input__warning-button"
            color="link"
            onClick={handleClickWarningLinkText}
          >
            {warningLinkText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default DropdownInput;
